import React from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from "recharts";

/**
 * Hardcoded static report page (no API).
 * - Shows Overall Growth composed chart (stacked bars + line)
 * - Shows Branchwise small multiples for 4 main branches (Velachery, Vadapalani, Tambaram, Poonamallee)
 * - Also displays the original uploaded images for reference (paths provided)
 *
 * Save as: src/pages/PlacedReportStatic.jsx
 * Install: npm i recharts
 */

const overallData = [
  // months with stacked categories: CAD (black), EMB (light), IT (gold)
  { month: "Apr'25", CAD: 26, EMB: 15, IT: 11, overall: 52 },
  { month: "May'25", CAD: 30, EMB: 19, IT: 7, overall: 56 },
  { month: "June'25", CAD: 44, EMB: 8, IT: 5, overall: 57 },
  { month: "July'25", CAD: 43, EMB: 12, IT: 8, overall: 63 },
  { month: "Aug'25", CAD: 57, EMB: 10, IT: 7, overall: 74 },
  { month: "Sep'25", CAD: 65, EMB: 19, IT: 17, overall: 101 },
  { month: "Oct'25", CAD: 52, EMB: 12, IT: 23, overall: 87 },
  // skip months (will appear blank)
  { month: "2025-26 Total", CAD: 317, EMB: 84, IT: 89, overall: 490 }
];

// sample branch small-multiple data (approx numbers derived from your branch image)
const branchSamples = {
  Tambaram: [
    { month: "Apr'25", CAD: 11, EMB: 7, IT: 1, overall: 19 },
    { month: "May'25", CAD: 15, EMB: 9, IT: 1, overall: 25 },
    { month: "Jun'25", CAD: 16, EMB: 7, IT: 1, overall: 24 },
    { month: "Jul'25", CAD: 19, EMB: 3, IT: 5, overall: 27 },
    { month: "Aug'25", CAD: 18, EMB: 5, IT: 6, overall: 29 },
    { month: "Sep'25", CAD: 12, EMB: 8, IT: 6, overall: 26 },
    { month: "Oct'25", CAD: 14, EMB: 4, IT: 5, overall: 23 },
    { month: "2025-26 Total", CAD: 14, EMB: 5, IT: 4, overall: 23 }
  ],
  Vadapalani: [
    { month: "Apr'25", CAD: 6, EMB: 6, IT: 0, overall: 12 },
    { month: "May'25", CAD: 4, EMB: 0, IT: 1, overall: 5 },
    { month: "Jun'25", CAD: 20, EMB: 0, IT: 0, overall: 20 },
    { month: "Jul'25", CAD: 17, EMB: 1, IT: 2, overall: 20 },
    { month: "Aug'25", CAD: 15, EMB: 2, IT: 0, overall: 17 },
    { month: "Sep'25", CAD: 15, EMB: 4, IT: 7, overall: 26 },
    { month: "Oct'25", CAD: 13, EMB: 0, IT: 5, overall: 18 },
    { month: "2025-26 Total", CAD: 13, EMB: 3, IT: 4, overall: 18 }
  ],
  Poonamallee: [
    { month: "Apr'25", CAD: 4, EMB: 6, IT: 0, overall: 10 },
    { month: "May'25", CAD: 6, EMB: 0, IT: 0, overall: 6 },
    { month: "Jun'25", CAD: 4, EMB: 1, IT: 1, overall: 6 },
    { month: "Jul'25", CAD: 12, EMB: 2, IT: 3, overall: 17 },
    { month: "Aug'25", CAD: 12, EMB: 2, IT: 2, overall: 16 },
    { month: "Sep'25", CAD: 12, EMB: 4, IT: 7, overall: 23 },
    { month: "Oct'25", CAD: 10, EMB: 2, IT: 0, overall: 12 },
    { month: "2025-26 Total", CAD: 10, EMB: 2, IT: 1, overall: 13 }
  ],
  Velachery: [
    { month: "Apr'25", CAD: 6, EMB: 0, IT: 0, overall: 6 },
    { month: "May'25", CAD: 5, EMB: 1, IT: 0, overall: 6 },
    { month: "Jun'25", CAD: 5, EMB: 2, IT: 0, overall: 7 },
    { month: "Jul'25", CAD: 5, EMB: 2, IT: 0, overall: 7 },
    { month: "Aug'25", CAD: 10, EMB: 0, IT: 4, overall: 14 },
    { month: "Sep'25", CAD: 10, EMB: 4, IT: 3, overall: 17 },
    { month: "Oct'25", CAD: 7, EMB: 2, IT: 4, overall: 13 },
    { month: "2025-26 Total", CAD: 6, EMB: 2, IT: 11, overall: 19 }
  ]
};

// color map to approximate the image (black = CAD, light = EMB, gold = IT)
const COLORS_MAP = {
  CAD: "#000000",
  EMB: "#EEDC9A", // pale
  IT: "#F6C100"   // gold
};

const smallChartStyle = { width: "320px", height: "220px", margin: "10px" };

const SmallBranchChart = ({ title, data }) => {
  return (
    <div style={{ border: "1px solid #ddd", padding: 8, borderRadius: 6, background: "#fff" }}>
      <h4 style={{ margin: "0 0 8px 0", color: "#666", textTransform: "uppercase" }}>{title}</h4>
      <ResponsiveContainer width="100%" height={180}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" hide={true} />
          <YAxis />
          <Tooltip />
          <Bar dataKey="CAD" stackId="a" fill={COLORS_MAP.CAD} />
          <Bar dataKey="EMB" stackId="a" fill={COLORS_MAP.EMB} />
          <Bar dataKey="IT" stackId="a" fill={COLORS_MAP.IT} />
          <Line type="monotone" dataKey="overall" stroke="#2f5bd9" dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function PlacedReportStatic() {
  // create branchData array for small-multiples or custom grouping
  const branches = ["Tambaram", "Vadapalani", "Poonamallee", "Velachery"];

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", padding: 18, background: "#f6f6f6" }}>
      <h1 style={{ letterSpacing: 1, margin: 0 }}>
        <span style={{ color: "#222", fontWeight: 700 }}>PLACEMENT</span>{" "}
        <span style={{ color: "#F6C100", fontWeight: 700 }}>REPORT</span> OVERALL
      </h1>

      <div style={{ marginTop: 12, marginBottom: 20 }}>
        {/* preview of uploaded overall image */}
        <div style={{ marginBottom: 12 }}>
          <strong>Uploaded preview (Report growth):</strong>
          <div style={{ marginTop: 6 }}>
            <img
              alt="overall growth preview"
              src={"/mnt/data/overall growth.jpeg"}
              style={{ maxWidth: "100%", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            />
          </div>
        </div>

        {/* Reproduced composed chart (hardcoded) */}
        <div style={{ background: "#fff", padding: 12, borderRadius: 6, boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
          <h3 style={{ margin: "0 0 8px 0" }}>Placement Report — Overall </h3>

          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={overallData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="CAD" stackId="a" fill={COLORS_MAP.CAD} />
              <Bar dataKey="EMB" stackId="a" fill={COLORS_MAP.EMB} />
              <Bar dataKey="IT" stackId="a" fill={COLORS_MAP.IT} />
              <Line type="monotone" dataKey="overall" stroke="#2f5bd9" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Branchwise section */}
      <h2 style={{ marginTop: 24 }}>PLACEMENT GROWTH — BRANCHWISE</h2>
      <div style={{ marginBottom: 12 }}>
        <strong>Uploaded preview (branch layout):</strong>
        <div style={{ marginTop: 6 }}>
          <img
            alt="branch preview"
            src={"/mnt/data/branch.jpeg"}
            style={{ maxWidth: "100%", boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}
          />
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 14,
        alignItems: "start"
      }}>
        {branches.map((b) => (
          <SmallBranchChart key={b} title={b} data={branchSamples[b]} />
        ))}

        {/* add placeholders for other small panels to mimic your layout */}
        <div style={{ gridColumn: "span 1" }}>
          <SmallBranchChart title="Other Branches (sample)" data={[
            { month: "Apr'25", CAD: 4, EMB: 1, IT: 0, overall: 6 },
            { month: "May'25", CAD: 9, EMB: 0, IT: 1, overall: 10 },
            { month: "Jun'25", CAD: 0, EMB: 0, IT: 0, overall: 0 },
            { month: "2025-26 Total", CAD: 4, EMB: 1, IT: 0, overall: 5 }
          ]} />
        </div>

        <div style={{ gridColumn: "span 1" }}>
          <SmallBranchChart title="Ambathur (sample)" data={[
            { month: "Apr'25", CAD: 3, EMB: 0, IT: 0, overall: 3 },
            { month: "May'25", CAD: 4, EMB: 0, IT: 0, overall: 4 },
            { month: "Sep'25", CAD: 9, EMB: 1, IT: 0, overall: 10 },
            { month: "2025-26 Total", CAD: 4, EMB: 0, IT: 0, overall: 4 }
          ]} />
        </div>
      </div>

      <footer style={{ marginTop: 20, color: "#444" }}>
        <hr style={{ margin: "14px 0" }} />
        <div style={{ fontWeight: 600 }}>Our branches: Velachery, Vadapalani, Tambaram, Poonamallee</div>
      </footer>
    </div>
  );
}
